


































































import { Component, Ref, Vue, Watch } from "vue-property-decorator";
import BaseDialogComponent from "@/components/common/BaseDialog.vue";
import ConfirmationDialogComponent from "@/components/common/ConfirmationDialog.vue";
import { ClientMasterData, MasterDataField } from "@/data/models/ClientAdministrationRow";
import { updateClientMasterData } from "@/data/repository/ClientAdministrationRepository";
import { SnackbarModule } from "@/store/modules/SnackbarModule";
import {
  canSaveMasterData,
  changesIresType,
  isMasterDataFieldChanged,
  keptProjectNumberPrefix,
  MASTER_DATA_CLIENT_TYPES,
  masterDataChanges,
  masterDataErrorKey,
  masterDataFieldError,
  MasterDataForm,
  toMasterDataForm,
} from "@/utils/ClientMasterData";

/** What `show()` resolves with. */
export type MasterDataDialogResult = ClientMasterData | "CHANGED_MEANWHILE" | null;

const EMPTY_MASTER_DATA: ClientMasterData = {
  clientName: null,
  clientNumber: null,
  street: null,
  houseNumber: null,
  zip: null,
  city: null,
  clientType: null,
  iresProjectNumberPrefix: null,
};

/**
 * Edits a client's Stammdaten (backlog #38, slice 1): the identity fields IRES Admin's properties
 * screen edits. Only changed fields are sent, each with the value loaded, so IRES Cloud can refuse one
 * changed meanwhile (`updateClientAdministration`); its rules are the authority, these give the
 * messages.
 *
 * `show()` resolves with the saved Stammdaten, `CHANGED_MEANWHILE` when IRES Cloud refused because the
 * client changed since it was loaded (the caller reloads), or null when closed without saving.
 */
@Component({ components: { "base-dialog": BaseDialogComponent, "confirmation-dialog": ConfirmationDialogComponent } })
export default class ClientMasterDataDialogComponent extends Vue {
  @Ref("baseDialog")
  baseDialog!: BaseDialogComponent;

  @Ref("confirm")
  confirmDialog!: ConfirmationDialogComponent;

  busy = false;
  clientId = "";
  // Initialised, not `!`: the computeds below must track them from the first render (edit-mode-dialogs.md).
  stored: ClientMasterData = { ...EMPTY_MASTER_DATA };
  form: MasterDataForm = toMasterDataForm(EMPTY_MASTER_DATA);
  projectNumberPrefix: string | null = null;

  private resolve: ((result: MasterDataDialogResult) => void) | null = null;

  get clientTypeItems(): Array<{ value: string; label: string }> {
    return MASTER_DATA_CLIENT_TYPES.map((value) => ({
      value,
      label: this.$t(`admin.clients.detail.clientTypes.${value}`).toString(),
    }));
  }

  get hasChanged(): boolean {
    return Object.keys(masterDataChanges(this.stored, this.form)).length > 0;
  }

  get canSave(): boolean {
    return canSaveMasterData(this.stored, this.form);
  }

  get keptPrefix(): string | null {
    return keptProjectNumberPrefix(this.stored, this.form, this.projectNumberPrefix);
  }

  get iresTypeChanges(): boolean {
    return changesIresType(this.stored, this.form);
  }

  /** A field's message, only once it changed: a stored value that predates a rule is not an error. */
  errorFor(field: MasterDataField): string[] {
    const error = isMasterDataFieldChanged(field, this.stored, this.form) ? masterDataFieldError(field, this.form[field]) : null;
    return error ? [this.$t(`admin.clients.masterData.fieldErrors.${error}`).toString()] : [];
  }

  /**
   * @param clientId - the client.
   * @param stored - its Stammdaten as the detail loaded them.
   * @param projectNumberPrefix - the prefix its IRES project numbers use, null before the first number.
   */
  show(clientId: string, stored: ClientMasterData, projectNumberPrefix: string | null): Promise<MasterDataDialogResult> {
    this.clientId = clientId;
    this.projectNumberPrefix = projectNumberPrefix;
    this.stored = { ...stored };
    this.form = toMasterDataForm(stored);
    this.baseDialog.show({
      title: this.$t("admin.clients.masterData.editTitle").toString(),
      positiveButtonText: this.$t("common.save").toString(),
      negativeButtonText: this.$t("common.close").toString(),
      maxWidth: 600,
      persistent: true,
      closeOnSave: false,
    });
    return new Promise((resolve) => (this.resolve = resolve));
  }

  /** SCHLIEßEN while nothing changed, ABBRECHEN after (edit-mode-dialogs.md). */
  @Watch("hasChanged")
  onHasChanged(hasChanged: boolean) {
    this.baseDialog.negativeButtonText = this.$t(hasChanged ? "common.abort" : "common.close").toString();
  }

  /** SCHLIEßEN has already closed the dialog; ABBRECHEN asks first. */
  async onNegative() {
    if (!this.hasChanged) {
      this.finish(null);
      return;
    }
    const discard = await this.confirmDialog.show({
      title: this.$t("common.confirmationDialog.unsavedChangesTitle").toString(),
      message: this.$t("common.confirmationDialog.unsavedChangesMessage").toString(),
      acceptMessage: this.$t("common.abort").toString(),
      rejectMessage: this.$t("common.back").toString(),
    });
    if (discard) {
      this.close(null);
    }
  }

  async onSave() {
    if (!this.canSave) {
      return;
    }
    this.busy = true;
    try {
      const saved = await updateClientMasterData(this.clientId, masterDataChanges(this.stored, this.form));
      SnackbarModule.showSuccess({ key: "admin.clients.masterData.saved" });
      this.close(saved ?? { ...this.stored });
    } catch (error) {
      const key = masterDataErrorKey(error);
      SnackbarModule.showError({ key });
      // Someone changed the client meanwhile: close, so the caller reloads and the user starts from the new values.
      if (key.endsWith("CHANGED_MEANWHILE")) {
        this.close("CHANGED_MEANWHILE");
      }
    } finally {
      this.busy = false;
    }
  }

  close(result: MasterDataDialogResult) {
    this.baseDialog.isShowing = false;
    this.finish(result);
  }

  private finish(result: MasterDataDialogResult) {
    this.resolve?.(result);
    this.resolve = null;
  }
}
